/**
 * Thin typed wrapper around @anthropic-ai/sdk.
 *
 * All Anthropic SDK access in the app routes through this module. Feature code
 * should never import the SDK directly — see the no-restricted-imports rule in
 * eslint.config.js.
 *
 * The wrapper unifies:
 *   - Browser-allow + CORS-proxy boilerplate (every call site repeated it)
 *   - System / user message shape (single function, no message-array stitching)
 *   - Usage counting (single source for input/output tokens)
 *   - SDK error mapping (caller gets a plain Error, not an SDK-specific class)
 *
 * If the SDK ships a breaking change, this is the only file that needs to know.
 */

import Anthropic from '@anthropic-ai/sdk';

export interface AnthropicClientOptions {
  apiKey: string;
  /** CORS proxy URL — Anthropic's browser SDK requires one. */
  baseURL?: string;
}

export interface AnthropicTextRequest extends AnthropicClientOptions {
  model: string;
  systemPrompt?: string;
  userPrompt: string;
  /**
   * Sampling temperature 0–1. Accepted for call-site compatibility but NOT
   * forwarded to the API — the Claude 5 family (sonnet-5/opus-4-8/etc.)
   * rejects the field outright ("temperature is deprecated for this model").
   * Default sampling is used instead.
   */
  temperature: number;
  maxTokens: number;
  /** Optional abort signal for cancellation. */
  signal?: AbortSignal;
}

export interface AnthropicTextResponse {
  text: string;
  inputTokens: number;
  outputTokens: number;
  /** Echoes the model used for the call — handy for usage tracking. */
  model: string;
}

export function createAnthropicClient(opts: AnthropicClientOptions): Anthropic {
  return new Anthropic({
    apiKey: opts.apiKey,
    baseURL: opts.baseURL,
    dangerouslyAllowBrowser: true,
    // SDK default is 2 retries; bumped to 5 so transient Anthropic 5xx flakes
    // (esp. mid-album batch generations) don't drop tracks. SDK already does
    // exponential backoff + jitter and respects retry-after on 408/409/429/5xx.
    maxRetries: 5,
  });
}

/**
 * Plain text completion. JSON-mode is opt-in by the caller appending JSON
 * instructions to the prompt — Anthropic doesn't have a structured-output
 * primitive comparable to Gemini's responseSchema, so we don't expose one here.
 */
export async function generateText(req: AnthropicTextRequest): Promise<AnthropicTextResponse> {
  const client = createAnthropicClient({ apiKey: req.apiKey, baseURL: req.baseURL });
  const message = await client.messages.create(
    {
      model: req.model,
      max_tokens: req.maxTokens,
      // The Claude 5 family defaults to extended thinking, and for a long
      // system prompt (HOOKHOUSE_PRO_SYSTEM_PROMPT plus the full physics-
      // layer instructions) it can consume the ENTIRE max_tokens budget on
      // reasoning and stop with zero text content at all — confirmed live
      // against the same model family in server/services/multiProviderGenerate.ts's
      // Claude path. None of this app's generation tasks need deep
      // reasoning; disabling guarantees the full budget goes to output.
      thinking: { type: 'disabled' },
      // System prompt is marked cache_control so repeated calls with the same
      // static prompt (e.g. HOOKHOUSE_PRO_SYSTEM_PROMPT) hit Anthropic's
      // prompt cache instead of re-billing/re-processing it every request.
      // Must be a content-block array, not a plain string — cache_control
      // only attaches to a block, and a bare string can never be cached.
      ...(req.systemPrompt
        ? { system: [{ type: 'text' as const, text: req.systemPrompt, cache_control: { type: 'ephemeral' as const } }] }
        : {}),
      messages: [{ role: 'user', content: req.userPrompt }],
    },
    req.signal ? { signal: req.signal } : undefined,
  );

  // Don't assume content[0] is the text block — Claude 5 models can emit a
  // 'thinking' block first (extended reasoning), pushing the actual text to
  // a later index. Join all text-type blocks instead.
  const text = message.content
    .filter((b): b is Anthropic.TextBlock => b.type === 'text')
    .map(b => b.text)
    .join('\n');
  return {
    text,
    inputTokens: message.usage?.input_tokens ?? 0,
    outputTokens: message.usage?.output_tokens ?? 0,
    model: req.model,
  };
}
